import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

/**
 * TicketWorkTimer Component
 * Allows users to track work time on a ticket with start/stop functionality
 * Usage: Display inside ticket detail page
 */
const TicketWorkTimer = ({ ticketMasterId, authToken }) => {
  const [workSessionId, setWorkSessionId] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [workSessions, setWorkSessions] = useState([]);
  const [totalWorkTime, setTotalWorkTime] = useState(0);
  const [userId, setUserId] = useState(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Get current user ID from token
  useEffect(() => {
    const token = authToken;
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        setUserId(decoded.userId || decoded.sub);
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
  }, [authToken]);

  // Fetch work sessions for this ticket
  const fetchWorkSessions = async () => {
    try {
      if (!userId) return;

      const response = await axios.get(
        `/api/ticket/workSession/user/${ticketMasterId}/${userId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        setWorkSessions(response.data.data || []);
        setTotalWorkTime(response.data.totalTimeMinutes || 0);
      }
    } catch (error) {
      console.error('Error fetching work sessions:', error);
    }
  };

  useEffect(() => {
    if (userId && ticketMasterId) {
      fetchWorkSessions();
      // Refresh every 30 seconds
      const interval = setInterval(fetchWorkSessions, 30000);
      return () => clearInterval(interval);
    }
  }, [userId, ticketMasterId, authToken]);

  // Start work session
  const startWorkSession = async () => {
    try {
      const response = await axios.post(
        '/api/ticket/workSession/start',
        { ticketMasterId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        const startTime = new Date();
        startTimeRef.current = startTime;
        setWorkSessionId(response.data.data.workSessionId);
        setTimerRunning(true);
        setElapsedTime(0);

        // Start interval
        timerRef.current = setInterval(() => {
          const now = new Date();
          const elapsed = Math.floor((now - startTime) / 1000);
          setElapsedTime(elapsed);
        }, 1000);
      }
    } catch (error) {
      console.error('Error starting work session:', error);
      alert('Error starting work timer');
    }
  };

  // Stop work session
  const stopWorkSession = async () => {
    try {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      const response = await axios.post(
        '/api/ticket/workSession/stop',
        { workSessionId, ticketMasterId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        setTimerRunning(false);
        setElapsedTime(0);
        setWorkSessionId(null);

        // Refresh sessions
        await fetchWorkSessions();
      }
    } catch (error) {
      console.error('Error stopping work session:', error);
      alert('Error stopping work timer');
    }
  };

  // Format seconds to HH:MM:SS
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Format minutes to readable format
  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };


};

export default TicketWorkTimer;
